import type { BrandVariants } from '@fluentui/react-components';

/**
 * COMLINE-Primärfarbe (Corporate-Orange laut Brand-Guide 2025). Über die
 * zentralen Einstellungen pflegbar (datengetrieben) — eine Änderung des
 * Primärfarb-Hex erzeugt zur Laufzeit eine neue Fluent-Brand-Rampe.
 */
export const COMLINE_PRIMARY = '#D84F22';

const STOPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] as const;

// Ziel-Helligkeit (HSL L in %), von dunkel (10) nach hell (160).
const LIGHTNESS = [9, 14, 19, 24, 29, 34, 39, 44, 50, 56, 62, 68, 75, 82, 89, 95];

/**
 * Erzeugt aus einem einzelnen Primärfarb-Hex eine vollständige
 * Fluent-Brand-Rampe (16 Stufen) durch Variation der Helligkeit.
 */
export function generateBrandRamp(primaryHex: string): BrandVariants {
  const { h, s } = hexToHsl(primaryHex);
  const ramp: Record<number, string> = {};
  STOPS.forEach((stop, i) => {
    ramp[stop] = hslToHex(h, clamp(s, 0.25, 0.95), LIGHTNESS[i] / 100);
  });
  return ramp as unknown as BrandVariants;
}

export const comlineBrand: BrandVariants = generateBrandRamp(COMLINE_PRIMARY);

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) {
    r = c;
    g = x;
  } else if (hue < 120) {
    r = x;
    g = c;
  } else if (hue < 180) {
    g = c;
    b = x;
  } else if (hue < 240) {
    g = x;
    b = c;
  } else if (hue < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
